import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [identities, setIdentities] = useState([]); // other roles linked to this login (e.g. teacher + parent)
  const [loading, setLoading] = useState(true);
  const [mustChangePassword, setMustChangePassword] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem('sms_auth');
    if (stored) {
      try {
        const user = JSON.parse(stored);
        setCurrentUser(user);
        if (user.mustChangePassword) setMustChangePassword(true);
        if (Array.isArray(user.identities)) setIdentities(user.identities);
        // Refresh in the background in case identities changed since last login
        fetch('/api/auth/identities', { headers: { Authorization: `Bearer ${user.token}` } })
          .then(r => r.ok ? r.json() : null)
          .then(list => { if (Array.isArray(list)) setIdentities(list); })
          .catch(() => {});
      } catch {}
    }
    setLoading(false);
  }, []);

  const login = async (email, password) => {
    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!res.ok) return { success: false, error: data.message || 'Invalid email or password' };
      const user = { ...data.user, token: data.token, mustChangePassword: !!data.mustChangePassword, identities: data.identities || [] };
      setCurrentUser(user);
      setIdentities(data.identities || []);
      setMustChangePassword(!!data.mustChangePassword);
      localStorage.setItem('sms_auth', JSON.stringify(user));
      window.dispatchEvent(new StorageEvent('storage', { key: 'sms_auth' }));
      return { success: true, user, mustChangePassword: !!data.mustChangePassword };
    } catch (e) {
      return { success: false, error: 'Cannot connect to server. Please try again.' };
    }
  };

  // Switch to another role/profile linked to this same login (one login, multiple
  // roles — e.g. a teacher who is also a parent). Re-issues a scoped token; the
  // caller is responsible for reloading role-scoped app data afterward.
  const switchIdentity = async (role, profileId) => {
    try {
      const res = await fetch('/api/auth/switch-identity', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${currentUser?.token}` },
        body: JSON.stringify({ role, profileId }),
      });
      const data = await res.json();
      if (!res.ok) return { success: false, error: data.message || 'Could not switch identity' };
      const user = { ...currentUser, ...data.user, token: data.token, mustChangePassword: false, identities };
      setCurrentUser(user);
      localStorage.setItem('sms_auth', JSON.stringify(user));
      window.dispatchEvent(new StorageEvent('storage', { key: 'sms_auth' }));
      return { success: true, user };
    } catch (e) {
      return { success: false, error: 'Cannot connect to server. Please try again.' };
    }
  };

  const logout = () => {
    setCurrentUser(null);
    setIdentities([]);
    setMustChangePassword(false);
    localStorage.removeItem('sms_auth');
  };

  const refreshCurrentUser = (updatedUser) => {
    const user = { ...currentUser, ...updatedUser };
    setCurrentUser(user);
    localStorage.setItem('sms_auth', JSON.stringify(user));
  };

  const clearMustChangePassword = () => {
    setMustChangePassword(false);
    if (currentUser) {
      const user = { ...currentUser, mustChangePassword: false };
      setCurrentUser(user);
      localStorage.setItem('sms_auth', JSON.stringify(user));
    }
  };

  if (loading) return null;

  return (
    <AuthContext.Provider value={{ currentUser, login, logout, refreshCurrentUser, mustChangePassword, clearMustChangePassword, identities, switchIdentity }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
